/**
 * Inspiration System Utility
 *
 * Implements D&D 5e inspiration mechanics with auto-prompt on failed rolls
 *
 * [MASTER PLAN]: Week 2, Day 10 - Missing Systems
 * [CREATED]: 2025-10-09
 */

/**
 * Check if inspiration should be offered for a failed roll
 *
 * @param {number} rollResult - The roll result (1d20 + modifiers)
 * @param {number} dc - The DC (difficulty class) to beat
 * @param {number} inspirationCount - Number of inspiration points available
 * @param {number} threshold - Maximum failure margin to offer inspiration (default: 3)
 * @returns {boolean} - Whether to offer inspiration
 */
export const shouldOfferInspiration = (rollResult, dc, inspirationCount, threshold = 3) => {
  // No inspiration available
  if (inspirationCount <= 0) return false;

  // Roll succeeded
  if (rollResult >= dc) return false;

  // Calculate failure margin
  const failureMargin = dc - rollResult;

  // Only offer if failed within threshold
  return failureMargin > 0 && failureMargin <= threshold;
};

/**
 * Check if inspiration auto-prompt is enabled in settings
 *
 * @param {Object} settings - User settings object
 * @returns {boolean} - Whether auto-prompt is enabled
 */
export const isInspirationAutoPromptEnabled = (settings) => {
  return settings?.inspirationAutoPrompt !== false; // Default to enabled
};

/**
 * Get the inspiration threshold from settings
 *
 * @param {Object} settings - User settings object
 * @returns {number} - Threshold value (default: 3)
 */
export const getInspirationThreshold = (settings) => {
  return settings?.inspirationThreshold || 3;
};

/**
 * Format inspiration prompt message
 *
 * @param {number} rollResult - The roll result
 * @param {number} dc - The DC to beat
 * @param {number} failureMargin - How much the roll failed by
 * @param {string} rollType - Type of roll (e.g., "ability check", "saving throw", "attack roll")
 * @returns {string} - Formatted prompt message
 */
export const formatInspirationPrompt = (rollResult, dc, failureMargin, rollType = 'roll') => {
  return `Your ${rollType} failed by ${failureMargin}! (${rollResult} vs DC ${dc})\n\nWould you like to spend Inspiration to reroll?`;
};

/**
 * Roll types that can use inspiration (D&D 5e rules)
 *
 * Inspiration can be used on:
 * - Ability checks
 * - Attack rolls
 * - Saving throws
 *
 * Inspiration CANNOT be used on:
 * - Damage rolls
 * - Hit dice
 * - Initiative (debatable by DM)
 */
export const INSPIRATION_VALID_ROLL_TYPES = {
  ABILITY_CHECK: 'ability_check',
  ATTACK_ROLL: 'attack_roll',
  SAVING_THROW: 'saving_throw',
  SKILL_CHECK: 'skill_check',
  INITIATIVE: 'initiative' // Optional, DM discretion
};

/**
 * Check if a roll type is valid for inspiration use
 *
 * @param {string} rollType - The type of roll
 * @returns {boolean} - Whether inspiration can be used
 */
export const canUseInspirationOnRollType = (rollType) => {
  return Object.values(INSPIRATION_VALID_ROLL_TYPES).includes(rollType);
};

/**
 * Create an inspiration prompt dialog data object
 *
 * @param {Object} rollData - Roll data object
 * @param {number} rollData.result - Roll result
 * @param {number} rollData.dc - DC to beat
 * @param {string} rollData.type - Roll type
 * @param {string} rollData.description - Roll description
 * @param {number} inspirationCount - Available inspiration
 * @param {Function} onConfirm - Callback when player confirms
 * @param {Function} onDecline - Callback when player declines
 * @returns {Object} - Dialog data object
 */
export const createInspirationPrompt = (rollData, inspirationCount, onConfirm, onDecline) => {
  const failureMargin = rollData.dc - rollData.result;

  return {
    title: '✨ Use Inspiration?',
    message: formatInspirationPrompt(rollData.result, rollData.dc, failureMargin, rollData.type),
    rollData,
    inspirationCount,
    onConfirm,
    onDecline
  };
};

/**
 * Helper to consume inspiration and track usage
 *
 * @param {number} currentInspiration - Current inspiration count
 * @param {Function} logActivity - Activity logging function
 * @param {string} rollDescription - Description of what roll used inspiration
 * @returns {number} - New inspiration count
 */
export const consumeInspiration = (currentInspiration, logActivity, rollDescription = 'a roll') => {
  if (currentInspiration <= 0) return 0;

  const newCount = currentInspiration - 1;

  // Log to activity feed
  if (logActivity) {
    logActivity(`Used Inspiration on ${rollDescription}`, 'character', {
      inspirationRemaining: newCount
    });
  }

  return newCount;
};

/**
 * Award inspiration to player
 *
 * @param {number} currentInspiration - Current inspiration count
 * @param {number} maxInspiration - Maximum inspiration (default: 1 per PHB 2024 RAW "Only One at a Time")
 * @param {Function} logActivity - Activity logging function
 * @param {string} reason - Why inspiration was awarded
 * @returns {number} - New inspiration count
 */
export const awardInspiration = (currentInspiration, maxInspiration = 1, logActivity, reason = 'great roleplay') => {
  if (currentInspiration >= maxInspiration) {
    return currentInspiration; // Already at max
  }

  const newCount = currentInspiration + 1;

  // Log to activity feed
  if (logActivity) {
    logActivity(`Gained Inspiration for ${reason}!`, 'character', {
      inspirationCount: newCount
    });
  }

  return newCount;
};

/**
 * Default inspiration settings
 */
export const DEFAULT_INSPIRATION_SETTINGS = {
  inspirationAutoPrompt: true,
  inspirationThreshold: 3,
  maxInspiration: 1
};

/**
 * Validate inspiration settings
 *
 * @param {Object} settings - Settings to validate
 * @returns {Object} - Validated settings with defaults applied
 */
export const validateInspirationSettings = (settings) => {
  return {
    inspirationAutoPrompt: settings?.inspirationAutoPrompt !== undefined
      ? Boolean(settings.inspirationAutoPrompt)
      : DEFAULT_INSPIRATION_SETTINGS.inspirationAutoPrompt,
    inspirationThreshold: typeof settings?.inspirationThreshold === 'number' && settings.inspirationThreshold >= 1
      ? settings.inspirationThreshold
      : DEFAULT_INSPIRATION_SETTINGS.inspirationThreshold,
    maxInspiration: typeof settings?.maxInspiration === 'number' && settings.maxInspiration >= 1
      ? settings.maxInspiration
      : DEFAULT_INSPIRATION_SETTINGS.maxInspiration
  };
};

export default {
  shouldOfferInspiration,
  isInspirationAutoPromptEnabled,
  getInspirationThreshold,
  formatInspirationPrompt,
  INSPIRATION_VALID_ROLL_TYPES,
  canUseInspirationOnRollType,
  createInspirationPrompt,
  consumeInspiration,
  awardInspiration,
  DEFAULT_INSPIRATION_SETTINGS,
  validateInspirationSettings
};
